import { NgZone } from '@angular/core';
import { DataSource, ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollRepeater, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { OrganisationRow } from '../models/organisations';

export const ORGANISATION_ROW_HEIGHT = 52;

export type OrganisationVirtualRow = OrganisationRow & { rowIndex: number };

// Same fixed-size CdkVirtualScrollRepeater wrapper as ContactsVirtualScrollDataSource (see
// contacts/VIRTUAL_SCROLL.md for the full rationale/gotchas) — a plain mat-table doesn't
// virtualize its rows on its own, so this DataSource slices the full row array down to whatever
// the viewport's renderedRangeStream says is currently visible.
export class OrganisationsVirtualScrollDataSource
  extends DataSource<OrganisationVirtualRow>
  implements CdkVirtualScrollRepeater<OrganisationVirtualRow>
{
  readonly dataStream: Observable<readonly OrganisationVirtualRow[]>;

  private readonly rows$ = new BehaviorSubject<OrganisationVirtualRow[]>([]);
  private readonly disconnected$ = new Subject<void>();
  // See contacts/VIRTUAL_SCROLL.md "Gotcha #1" — CdkTable calls connect() before the viewport's
  // @ViewChild setter fires, so the viewport is buffered through a ReplaySubject and switchMap-ed
  // into rather than read from a plain nullable field.
  private readonly viewport$ = new ReplaySubject<CdkVirtualScrollViewport>(1);

  constructor(private readonly ngZone: NgZone) {
    super();
    this.dataStream = this.rows$.asObservable();
  }

  setData(rows: OrganisationRow[]) {
    this.rows$.next(rows.map((row, rowIndex) => ({ ...row, rowIndex })));
  }

  attachViewport(viewport: CdkVirtualScrollViewport) {
    viewport.attach(this);
    this.viewport$.next(viewport);
  }

  // Required by CdkVirtualScrollRepeater, but unreachable — the viewport uses [itemSize]
  // (FixedSizeVirtualScrollStrategy), which never calls this.
  measureRangeSize(range: ListRange): number {
    return (range.end - range.start) * ORGANISATION_ROW_HEIGHT;
  }

  connect(): Observable<readonly OrganisationVirtualRow[]> {
    return this.emitInAngularZone(
      this.viewport$.pipe(
        switchMap(viewport =>
          combineLatest([this.rows$, viewport.renderedRangeStream.pipe(startWith({ start: 0, end: 0 }))])
        ),
        map(([rows, range]) => rows.slice(range.start, range.end)),
        takeUntil(this.disconnected$)
      )
    );
  }

  disconnect() {
    this.disconnected$.next();
    this.disconnected$.complete();
  }

  // The viewport registers its scroll listener, and subscribes to dataStream, from inside
  // ngZone.runOutsideAngular() — see CdkVirtualScrollViewport.attach() ("Run outside the zone to
  // avoid triggering change detection, since we're managing the change detection loop ourselves")
  // and the ScrollDispatcher's own listener. So every renderedRangeStream emission caused by a
  // scroll arrives OUTSIDE the Angular zone, and CdkTable renders its rows straight off the
  // stream connect() returns.
  //
  // That matters because zone.js captures whichever zone is current at the moment
  // addEventListener runs. Without this wrapper, the (click) handler on every row rendered during
  // a scroll ends up bound outside the Angular zone: clicking such a row still runs openRow() and
  // still mutates component state, but no change detection follows, so nothing appears to happen
  // — until some unrelated in-zone event (the header row's own (scroll) binding, typically) ticks
  // change detection and flushes the state change all at once. On the Organisations grid that
  // read as "clicking an organisation does nothing, then scrolling opens it", with the detail
  // screen's GET firing only at that point, since it's issued from its ngOnChanges.
  //
  // Re-entering the zone here puts row rendering — and therefore listener registration — back
  // inside it. Rows rendered from the initial HTTP response were always fine; only scrolled-to
  // rows were affected, which is what made it look intermittent.
  private emitInAngularZone<T>(source: Observable<T>): Observable<T> {
    return new Observable<T>(subscriber =>
      source.subscribe({
        next: value => this.runInAngularZone(() => subscriber.next(value)),
        error: (error: unknown) => this.runInAngularZone(() => subscriber.error(error)),
        complete: () => this.runInAngularZone(() => subscriber.complete())
      })
    );
  }

  // Only pays for ngZone.run() when the emission actually came from outside — setData() and the
  // first render already run in the zone, and re-entering an zone you're already in would queue a
  // redundant change-detection tick per emission.
  private runInAngularZone(action: () => void) {
    if (NgZone.isInAngularZone()) {
      action();
      return;
    }
    this.ngZone.run(action);
  }
}
